import { styled } from "goober";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import type { IconName } from "../../assets/icons";
import { playClick } from "../../lib/sounds";
import { navigate, routes } from "../../lib/urls";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useShiftHeld } from "../../hooks/useShiftHeld";
import { ClickButton } from "../ClickButton";
import { FooterAction } from "../FooterAction";
import { Icon } from "../Icon";
import { Keycap } from "../Keycap";
import { KeyboardHint } from "../KeyboardHint";
import { SearchContainer, SearchInput } from "../SearchField";
import { Text } from "../Text";

const Container = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  overflow: hidden;
`;

const SectionHeader = styled("div")`
  display: flex;
  padding: 10px 8px 6px 8px;
  box-sizing: content-box;
  align-items: center;
  color: var(--text-secondary);
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  padding: 4px;
  gap: 4px;
  max-height: 240px;
  overflow-y: auto;
  @media (max-width: 768px) {
    max-height: 40dvh;
  }
`;

const Row = styled(ClickButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  padding-right: 28px;
  cursor: pointer;
  border-radius: 8px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const rowTextStyle = {
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
  flex-shrink: 0;
`;

type Suggestion = {
  id: string;
  icon: IconName;
  label: string;
  disabled?: boolean;
} & ({ prompt: string } | { run: () => void });

export type SuggestionSet = {
  header: string;
  items: readonly Suggestion[];
};

export type SkillItem = {
  disabled?: boolean;
  id: string;
  name: string;
  description: string;
};

type SlashCommand = {
  id: string;
  icon: IconName;
  label: string;
  run: () => void;
};

type FlatItem =
  | { type: "skill"; skill: SkillItem }
  | { type: "suggestion"; suggestion: Suggestion }
  | { type: "action"; action: SlashCommand };

type Props = {
  onSelect: (prompt: string) => void;
  onSelectSkill: (skill: SkillItem) => void;
  onDismiss: () => void;
  suggestions: SuggestionSet;
  skills: readonly SkillItem[];
  canCreateSkill: boolean;
  onCreateSkill: () => void;
};

export function SlashMenu({
  onSelect,
  onSelectSkill,
  onDismiss,
  suggestions,
  skills,
  canCreateSkill,
  onCreateSkill,
}: Props) {
  const isMobile = useIsMobile();
  const shiftHeld = useShiftHeld();
  const [query, setQuery] = useState("");
  const [rawSelectedIndex, setSelectedIndex] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const q = query.trim().toLowerCase();
  const filteredSkills = q
    ? skills.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q)
      )
    : skills;
  const filteredSuggestions = q
    ? suggestions.items.filter((s) => s.label.toLowerCase().includes(q))
    : suggestions.items;
  const matches: FlatItem[] = [
    ...filteredSkills.map((skill) => ({ type: "skill" as const, skill })),
    ...filteredSuggestions.map((suggestion) => ({
      type: "suggestion" as const,
      suggestion,
    })),
  ];

  // When nothing matches, offer somewhere to go rather than a dead end.
  const emptyActions: SlashCommand[] = [];
  if (matches.length === 0) {
    emptyActions.push({
      id: "browse",
      icon: "skills",
      label: "Browse skills",
      run: () => navigate(routes.skills.list, { q: query }),
    });
    if (canCreateSkill) {
      emptyActions.push({
        id: "create",
        icon: "plus",
        label: "Create a skill",
        run: onCreateSkill,
      });
    }
  }

  const flat: FlatItem[] = [
    ...matches,
    ...emptyActions.map((action) => ({ type: "action" as const, action })),
  ];
  // Clamp rather than store: the filtered list can shrink under the highlight.
  const selectedIndex = Math.min(
    rawSelectedIndex,
    Math.max(0, flat.length - 1)
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    bodyRef.current
      ?.querySelector('[data-highlighted="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const handleSelect = (index: number, shiftKey: boolean) => {
    const item = flat[index];
    if (!item) return;
    if (item.type === "skill") {
      if (item.skill.disabled) return;
      if (shiftKey) navigate(routes.modal.skill, { id: item.skill.id });
      else onSelectSkill(item.skill);
    } else if (item.type === "suggestion") {
      if (item.suggestion.disabled) return;
      if ("run" in item.suggestion) item.suggestion.run();
      else onSelect(item.suggestion.prompt);
    } else {
      item.action.run();
    }
    onDismiss();
  };

  const onMenuKeyDown = useEffectEvent((e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopImmediatePropagation();
      setSelectedIndex(Math.min(selectedIndex + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopImmediatePropagation();
      setSelectedIndex(Math.max(selectedIndex - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopImmediatePropagation();
      playClick();
      handleSelect(selectedIndex, e.shiftKey);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopImmediatePropagation();
      onDismiss();
    } else if (e.key === "Backspace" && query === "") {
      onDismiss();
    }
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => onMenuKeyDown(e);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  const renderRow = (
    index: number,
    key: string,
    icon: IconName,
    label: string,
    disabled = false,
    description?: string
  ) => {
    return (
      <Row
        key={key}
        disabled={disabled}
        data-no-pill
        onMouseEnter={() => setSelectedIndex(index)}
        onClick={(e) => handleSelect(index, e.shiftKey)}
        data-highlighted={index === selectedIndex}
      >
        <Icon name={icon} size={16} style={{ flexShrink: 0 }} />
        <div style={{ display: "grid", flex: 1, minWidth: 0 }}>
          <Text
            variant="text-regular"
            color="var(--text-primary)"
            style={rowTextStyle}
          >
            {label}
          </Text>
          {description && (
            <Text
              variant="text-small"
              color="var(--text-secondary)"
              style={rowTextStyle}
            >
              {description}
            </Text>
          )}
        </div>
        {index === selectedIndex && !disabled && (
          <KeyboardHint
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <Keycap>
              <Icon
                name={
                  shiftHeld && flat[index]?.type === "skill"
                    ? "arrow-up-right"
                    : "corner-down-left"
                }
                size={12}
              />
            </Keycap>
          </KeyboardHint>
        )}
      </Row>
    );
  };

  return (
    <Container>
      <Body ref={bodyRef}>
        {filteredSkills.length > 0 && (
          <>
            <SectionHeader>
              <Text variant="text-small">Skills</Text>
            </SectionHeader>
            {filteredSkills.map((skill, i) =>
              renderRow(
                i,
                `skill-${skill.id}`,
                "skills",
                skill.name,
                skill.disabled,
                skill.description
              )
            )}
          </>
        )}
        {filteredSuggestions.length > 0 && (
          <>
            <SectionHeader>
              <Text variant="text-small">{suggestions.header}</Text>
            </SectionHeader>
            {filteredSuggestions.map((suggestion, i) =>
              renderRow(
                filteredSkills.length + i,
                `suggestion-${suggestion.id}`,
                suggestion.icon,
                suggestion.label,
                suggestion.disabled
              )
            )}
          </>
        )}
        {emptyActions.map((action, i) =>
          renderRow(
            matches.length + i,
            `action-${action.id}`,
            action.icon,
            action.label
          )
        )}
      </Body>
      <SearchContainer
        style={{
          borderTop: "0.5px solid var(--border-alpha)",
          borderBottom: "none",
        }}
      >
        <Icon name="search" size={16} />
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder="Search skills and prompts…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Instructions>
          {isMobile ? (
            <FooterAction onPress={onDismiss}>
              <Text variant="text-smaller-md">Close</Text>
            </FooterAction>
          ) : (
            <>
              {flat[selectedIndex]?.type === "skill" && (
                <>
                  <KeyboardHint>
                    <Text variant="text-smaller-md">Open </Text>
                    <Keycap style={{ padding: "2px 4px", gap: 2 }}>
                      <Text variant="text-smaller-md">⇧</Text>
                      <Icon name="corner-down-left" size={12} />
                    </Keycap>
                  </KeyboardHint>
                  <Icon name="slash" size={12} color="var(--border-alpha)" />
                </>
              )}
              <KeyboardHint>
                <Keycap style={{ padding: "2px 4px" }}>
                  <Text variant="text-smaller-md">esc</Text>
                </Keycap>
              </KeyboardHint>
            </>
          )}
        </Instructions>
      </SearchContainer>
    </Container>
  );
}
